import { useCallback } from "react";
import url from "../../config/url";


const URL = `${url}plantas/rutas-por-usuario`;

const PARAMETROS_ELECTRICOS_ROUTES = new Set(["mission-ro-3", "mission-ro-4"]);
const NORMALIZACION_ROUTES = new Map([
  [160, "mission-ro-3"],
  [200, "mission-ro-3"],
  [180, "piscu"],
  [190, "mission-ro-4"],
  [230, "mission-ro-4"]
]);
const EMPRESA_LABELS = {
  31: "BLUEBERRIES PERÚ",
  150: "TA EXPORT",
  180: "PROSEMBRA",
  220: "BLUEWAY"
};
const PLANTA_LABELS = {
  200: "TERRANOVA 3",
  230: "TERRANOVA 4",
  220: "BLUEWAY"
};
const FORCED_UPPERCASE_LABELS = new Set(["BLUEWAY", "PROSEMBRA"]);

function isParametroElectrico(planta, route) {
  return (
    Number(planta?.parametro_electrico) === 1 ||
    PARAMETROS_ELECTRICOS_ROUTES.has(route)
  );
}

function shouldUseUppercaseForPlant(planta) {
  const plantCode = String(
    planta?.planta || planta?.pais || planta?.codigo_pais || ""
  )
    .trim()
    .toUpperCase();

  return plantCode === "PE";
}

function toUppercaseLabel(label, shouldUppercase) {
  if (!shouldUppercase) {
    return label;
  }

  return String(label || "").toUpperCase();
}

function normalizeForcedUppercaseLabel(label) {
  const normalized = String(label || "").trim();

  if (!normalized) {
    return normalized;
  }

  if (normalized.toUpperCase().startsWith("TERRANOVA")) {
    return normalized.toUpperCase();
  }

  if (FORCED_UPPERCASE_LABELS.has(normalized.toUpperCase())) {
    return normalized.toUpperCase();
  }

  return normalized;
}

function normalizeRoute(planta) {
  const normalizedRoute = String(planta?.ruta || "")
    .trim()
    .replace(/^#\/?/, "")
    .replace(/^\/+/, "");

  if (!normalizedRoute) {
    return null;
  }

  if (isParametroElectrico(planta, normalizedRoute)) {
    return `#/parametros-electricos/${normalizedRoute}`;
  }

  return `#/SCADAS/${normalizedRoute}`;
}

function getActionLabel(planta, route) {
  if (isParametroElectrico(planta, route)) {
    return "Parámetros eléctricos";
  }

  return "SCADA";
}

function getNormalizationRoute(planta) {
  const plantaId = Number(planta?.id_planta) || 0;
  const route = NORMALIZACION_ROUTES.get(plantaId);

  if (Number(planta?.normalizacion) !== 1 || !route) {
    return null;
  }

  return `#/normalizacion/${route}`;
}

function getCompanyLabel(planta, empresaId) {
  const label =
    EMPRESA_LABELS[empresaId] || planta?.nombre_empresa || `Empresa ${empresaId}`;
  return normalizeForcedUppercaseLabel(label);
}

function getPlantLabel(planta) {
  const plantaId = Number(planta?.id_planta) || 0;

  if (PLANTA_LABELS[plantaId]) {
    return PLANTA_LABELS[plantaId];
  }

  if (Number(planta?.related_to) > 0) {
    return normalizeForcedUppercaseLabel(
      planta?.nombre_planta_base || planta?.nombre
    );
  }

  return normalizeForcedUppercaseLabel(planta?.nombre || planta?.nombre_planta_base);
}

function buildRoutes(plantas) {
  const companies = new Map();

  plantas.forEach((planta) => {
    const rawRoute = String(planta?.ruta || "")
      .trim()
      .replace(/^\/+/, "");
    const to = normalizeRoute(planta);

    if (!to) {
      return;
    }

    const empresaId = Number(planta?.id_empresa) || 0;
    const companyLabel = getCompanyLabel(planta, empresaId);
    const companyKey = empresaId || companyLabel;
    const plantKey = Number(planta?.related_to) > 0
      ? Number(planta.related_to)
      : Number(planta?.id_planta) || to;
    const shouldUppercase = shouldUseUppercaseForPlant(planta);
    const plantLabel = toUppercaseLabel(getPlantLabel(planta), shouldUppercase);
    const actionLabel = toUppercaseLabel(
      getActionLabel(planta, rawRoute),
      shouldUppercase
    );

    if (!companies.has(companyKey)) {
      companies.set(companyKey, {
        icon: "pe-7s-network",
        label: companyLabel,
        content: [],
        plants: new Map()
      });
    }

    const company = companies.get(companyKey);

    if (!company.plants.has(plantKey)) {
      company.plants.set(plantKey, {
        label:
          plantLabel ||
          toUppercaseLabel(to.replace(/^#\//, ""), shouldUppercase),
        content: [],
        actionTargets: new Set()
      });
    }

    const plant = company.plants.get(plantKey);
    const actionKey = `${actionLabel}-${to}`;

    if (plant.actionTargets.has(actionKey)) {
      return;
    }

    plant.actionTargets.add(actionKey);
    plant.content.push({
      label: actionLabel,
      to
    });

    const normalizationRoute = getNormalizationRoute(planta);
    const normalizationLabel = toUppercaseLabel(
      "Normalización",
      shouldUppercase
    );
    const normalizationKey = `${normalizationLabel}-${normalizationRoute}`;

    if (normalizationRoute && !plant.actionTargets.has(normalizationKey)) {
      plant.actionTargets.add(normalizationKey);
      plant.content.push({
        label: normalizationLabel,
        to: normalizationRoute
      });
    }
  });

  const content = Array.from(companies.values()).map((company) => ({
    icon: company.icon,
    label: company.label,
    content: Array.from(company.plants.values()).map((plant) => ({
      label: plant.label,
      content: plant.content
    }))
  }));

  if (content.length === 0) {
    return [];
  }

  return [
    {
      icon: "pe-7s-network",
      label: "PLANTAS",
      content
    }
  ];
}

export function useGetIdPlantasByClientUser() {
  const getIdPlantasByClientUser = useCallback(async (idUser) => {
    try {
      const response = await fetch(URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify({ idUsuario: idUser })
      });

      const json = await response.json();

      const plantas = Array.isArray(json?.data) ? json.data : [];

      return {
        ...json,
        routes: buildRoutes(plantas)
      };
    } catch (error) {
      console.error(error);
      return {
        status: "error",
        data: [],
        routes: []
      };
    }
  }, []);

  return { getIdPlantasByClientUser };
}
